import { Component, ElementRef, Input, ViewChild, ViewEncapsulation } from '@angular/core';
import * as moment from 'moment';

import { configureTooltips } from '../../chartjs.config';

interface Vec2 {
  x: number;
  y: number;
}

declare var Chart;

@Component({
  selector: 'ca-graphs',
  encapsulation: ViewEncapsulation.None,
  templateUrl: 'graphs.component.html',
  styleUrls: ['./graphs.component.scss']
})
export class GraphsComponent {
  @ViewChild('typeChart')
  typeChart: ElementRef;
  @ViewChild('typeChartContainer')
  typeChartContainer: ElementRef;

  @ViewChild('categoryChart')
  categoryChart: ElementRef;
  @ViewChild('categoryChartContainer')
  categoryChartContainer: ElementRef;

  readonly TYPE_CHART_OPTIONS = {
    cutoutPercentage: 70,
    showAllTooltips: true,
    elements: { arc: { borderWidth: 8 } },
    tooltips: {
      callbacks: {
        title: (items, data) => {
          return data.labels[items[0].index];
        }
      },
      custom: tooltipModel => {
        if (tooltipModel._data.datasets[0].data[tooltipModel._model.dataPoints[0].index] <= 0) {
          return;
        }
        let id = `graph-tooltip-${tooltipModel._model.title[0]}`;
        let tooltipEl = document.getElementById(id);
        if (!tooltipEl) {
          tooltipEl = document.createElement('div');
          tooltipEl.id = id;
          tooltipEl.classList.add('tooltip');
          tooltipEl.classList.add(`ca-priority-${tooltipModel._model.title[0]}`);
        }

        var canvasPosition = this.typeChart.nativeElement.getBoundingClientRect();
        var containerPos = this.typeChartContainer.nativeElement.getBoundingClientRect();

        let newPos = this.calculateNewIconPosition(
          { x: tooltipModel._model.caretX, y: tooltipModel._model.caretY },
          { x: canvasPosition.width, y: canvasPosition.height },
          15
        );

        // We need to offset by the parent's viewport position
        tooltipEl.style.left = canvasPosition.left - containerPos.left + newPos.x + 'px';
        tooltipEl.style.top = canvasPosition.top - containerPos.top + newPos.y + 'px';
        this.typeChartContainer.nativeElement.appendChild(tooltipEl);
      }
    }
  };

  readonly CATEGORY_CHART_OPTIONS = {
    cutoutPercentage: 70,
    showAllTooltips: true,
    elements: { arc: { borderWidth: 8 } },
    tooltips: {
      callbacks: {
        title: (items, data) => {
          return data.labels[items[0].index];
        }
      },
      custom: tooltipModel => {
        if (tooltipModel._data.datasets[0].data[tooltipModel._model.dataPoints[0].index] <= 0) {
          return;
        }
        let id = `graph-tooltip-${tooltipModel._model.title[0]}`;
        let tooltipEl = document.getElementById(id);
        if (!tooltipEl) {
          tooltipEl = document.createElement('div');
          tooltipEl.id = id;
          tooltipEl.classList.add('tooltip');
          tooltipEl.classList.add(`ca-priority-nil`);
          tooltipEl.classList.add(`${tooltipModel._model.title[0]}`);
        }

        var canvasPosition = this.categoryChart.nativeElement.getBoundingClientRect();
        var containerPos = this.categoryChartContainer.nativeElement.getBoundingClientRect();

        let newPos = this.calculateNewIconPosition(
          { x: tooltipModel._model.caretX, y: tooltipModel._model.caretY },
          { x: canvasPosition.width, y: canvasPosition.height },
          15
        );

        // We need to offset by the parent's viewport position
        tooltipEl.style.left = canvasPosition.left - containerPos.left + newPos.x + 'px';
        tooltipEl.style.top = canvasPosition.top - containerPos.top + newPos.y + 'px';
        this.categoryChartContainer.nativeElement.appendChild(tooltipEl);
      }
    }
  };

  readonly LINE_CHART_OPTIONS = {
    scales: {
      xAxes: [{ display: false }],
      yAxes: [{ display: false, gridLines: { display: false } }]
    },
    elements: { point: { radius: 0 }, line: { tension: 0, fill: false } },
    fill: false,
    responsive: true,
    layout: {
      padding: {
        right: 7,
        bottom: 5,
        top: 7
      }
    }
  };

  readonly LINE_COLORS = [
    {
      backgroundColor: ['#50E2C1'],
      borderColor: ['#50E2C1'],
      pointBorderColor: '#3C4249',
      pointBackgroundColor: '#3C4249'
    }
  ];
  readonly PIE_COLORS = [
    {
      backgroundColor: new Array(12).fill('#4FB1A6')
    }
  ];

  typeLabels;
  typeData = [];

  categoryLabels;
  categoryData = [];

  dailyMemos;
  dailyLabels: Array<any>;
  totalMemos: number;

  /**
   * Either ng2-charts or ChartJS really doesn't like data changing once the chart has
   * been created.
   */
  dataReady = false;

  /**
   * Calculates a new position for an icon on a donut chart.
   *
   * This is fairly simple 2D vector maths. First we calculate the centrepoint of the canvas.
   * Next, we calculate a direction vector from the centrepoint to the tooltip old location.
   * Next, we calculate a new position on the vector based on the offset
   *
   * @param old The Icon position as calculated by GraphJS
   * @param bounds The width and height of the canvas where the chart is rendered
   * @param offset How many pixels should the icon be pushed in/out from the centre of the chart
   */
  calculateNewIconPosition(old: Vec2, bounds: Vec2, offset: number): Vec2 {
    // calculate centre of canvas
    var centerX = bounds.x / 2;
    var centerY = bounds.y / 2;

    // direction vector from centre to old icon position
    var dX = old.x - centerX;
    var dY = old.y - centerY;

    // the distance from the centre to the old icon position
    var l = Math.sqrt(dX * dX + dY * dY);

    // make a new position along the direction vector, offset by an amount
    return {
      x: centerX + (dX / l) * (l + offset),
      y: centerY + (dY / l) * (l + offset)
    };
  }

  @Input()
  set data(newData: {
    dates: { [id: string]: number };
    categories: { [id: string]: number };
    types: { [id: string]: number };
    total: number;
    categoryDetails: { entities: { [id: string]: any } };
  }) {
    if (!newData) {
      return;
    }
    try {
      if (!newData.types || !newData.categories) {
        return;
      }
      this.typeLabels = Object.getOwnPropertyNames(newData.types);
      this.typeData = Object.getOwnPropertyNames(newData.types).map(value => newData.types[value]);

      this.categoryLabels = Object.getOwnPropertyNames(newData.categories).map(id => {
        return newData.categoryDetails[id].icon;
      });
      this.categoryData = Object.getOwnPropertyNames(newData.categories).map(
        value => newData.categories[value]
      );

      // ng2-charts freaks out if there are no categories
      // See https://gitlab.nextfaze.com/careapp/careapp-dashboard-angular/issues/45
      if (this.categoryData.length === 0) {
        this.categoryData = [newData.total];
        this.categoryLabels = ['All'];
      }

      this.dailyLabels = this.getDailyLabels();
      let pointRadius = new Array(this.dailyLabels.length).fill(0);
      pointRadius[pointRadius.length - 1] = 6;
      this.dailyMemos = [
        {
          data: [...this.dailyLabels.map(label => newData.dates[label] || 0)],
          pointRadius: pointRadius,
          label: 'Memos per day'
        }
      ];
      this.totalMemos = newData.total;
      this.dataReady = true;
    } catch (e) {
      return;
    }
  }

  constructor() {
    configureTooltips();
  }

  getDailyLabels() {
    let days = Array(30);
    for (let i = 0; i < days.length; i++) {
      days[i] = days.length - 1 - i;
    }
    return days.map(day =>
      moment()
        .subtract(day, 'days')
        .format('YYYY-MM-DD')
    );
  }
}
